import React, { useCallback, useEffect, useState } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import { Plus, Trash2, ChevronDown, ChevronRight, GripVertical } from "lucide-react";

const LEVELS = ["LGS", "TYT", "AYT"];

/**
 * Müfredat yönetimi — 3 seviyeli hiyerarşi.
 *
 * Native HTML5 drag&drop:
 *  - Ana konuyu gruba bırak → assign-group
 *  - Gruplar arası sıra: grup başlığına bırak → reorder
 *  - Grup içinde ana konu sırası: karta bırak → reorder
 *  - Alt konu sırası: satıra bırak → subtopics/reorder
 * Alt konu CRUD zaten /admin/konular sayfasında var; burası düzenleme + gruplama.
 */
export default function AdminCurriculum() {
  const [level, setLevel] = useState("TYT");
  const [tree, setTree] = useState({ groups: [], ungrouped: [] });
  const [newGroup, setNewGroup] = useState("");
  const [open, setOpen] = useState({});
  const [drag, setDrag] = useState(null); // {kind:'topic'|'group', id}

  const load = useCallback(async () => {
    try { setTree((await api.get(`/catalog/curriculum?exam_level=${level}`)).data); }
    catch { setTree({ groups: [], ungrouped: [] }); }
  }, [level]);
  useEffect(() => { load(); }, [load]);

  const addGroup = async () => {
    if (!newGroup.trim()) return;
    try {
      await api.post("/catalog/curriculum-groups", { name: newGroup.trim(), exam_level: level });
      setNewGroup(""); toast.success("Grup eklendi"); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Eklenemedi"); }
  };

  const renameGroup = async (g) => {
    const name = window.prompt("Grup adı", g.name);
    if (!name || name === g.name) return;
    try { await api.put(`/catalog/curriculum-groups/${g.group_id}`, { name }); toast.success("Güncellendi"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Güncellenemedi"); }
  };

  const deleteGroup = async (g) => {
    if (!window.confirm(`"${g.name}" grubunu silmek istiyor musun? Konular korunur, gruptan çıkarılır.`)) return;
    try { await api.delete(`/catalog/curriculum-groups/${g.group_id}`); toast.success("Grup silindi"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Silinemedi"); }
  };

  const assignToGroup = async (topic_id, group_id) => {
    try { await api.post(`/catalog/topics/${topic_id}/assign-group`, { group_id }); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Atanamadı"); }
  };

  const reorderGroups = async (ids) => {
    try { await api.post("/catalog/curriculum-groups/reorder", { ids }); load(); }
    catch { toast.error("Sıralanamadı"); }
  };
  const reorderTopics = async (ids) => {
    try { await api.post("/catalog/topics/reorder", { ids }); load(); }
    catch { toast.error("Sıralanamadı"); }
  };
  const reorderSubs = async (ids) => {
    try { await api.post("/catalog/subtopics/reorder", { ids }); load(); }
    catch { toast.error("Sıralanamadı"); }
  };

  const onDragStart = (kind, id) => (e) => {
    setDrag({ kind, id });
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", id);
  };
  const onDragOver = (e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; };

  // Drop bir grup başlığına: sürüklenen topic ise → o gruba ata; grup ise → grup sıralamada bu grubun önüne
  const dropOnGroup = (target) => (e) => {
    e.preventDefault(); if (!drag) return;
    if (drag.kind === "topic") { assignToGroup(drag.id, target.group_id); }
    else if (drag.kind === "group" && drag.id !== target.group_id) {
      const order = tree.groups.map((g) => g.group_id).filter((id) => id !== drag.id);
      const idx = order.indexOf(target.group_id);
      order.splice(idx, 0, drag.id);
      reorderGroups(order);
    }
    setDrag(null);
  };
  // Drop bir ana konu satırına: aynı grup içindeyse → o grup içinde sırala; farklı grupsa → sürüklenen topic'i hedef grubun bulunduğu gruba ata + sırala
  const dropOnTopic = (target, groupTopics) => (e) => {
    e.preventDefault(); if (!drag || drag.kind !== "topic" || drag.id === target.topic_id) return;
    const ids = groupTopics.map((t) => t.topic_id).filter((id) => id !== drag.id);
    const idx = ids.indexOf(target.topic_id);
    ids.splice(idx, 0, drag.id);
    reorderTopics(ids);
    setDrag(null);
  };
  const dropOnSub = (target, siblings) => (e) => {
    e.preventDefault(); if (!drag || drag.kind !== "sub" || drag.id === target.subtopic_id) return;
    const ids = siblings.map((s) => s.subtopic_id).filter((id) => id !== drag.id);
    const idx = ids.indexOf(target.subtopic_id);
    ids.splice(idx, 0, drag.id);
    reorderSubs(ids); setDrag(null);
  };
  const dropDetachToUngrouped = (e) => {
    e.preventDefault();
    if (drag?.kind === "topic") { assignToGroup(drag.id, null); setDrag(null); }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="font-display text-3xl tracking-tight">Müfredat Yönetimi</h1>
        <div className="flex gap-2">
          {LEVELS.map((l) => (
            <button key={l} onClick={() => setLevel(l)} data-testid={`admin-curr-level-${l.toLowerCase()}`}
                    className={`px-4 py-2 rounded-lg text-sm ${level === l ? "bg-[#EA580C] text-white" : "border border-[#262626] text-slate-300"}`}>
              {l}
            </button>
          ))}
        </div>
      </div>

      <section className="bg-[#171717] border border-[#262626] rounded-lg p-4">
        <div className="text-sm font-medium mb-2 text-slate-200">Yeni üst grup</div>
        <div className="flex gap-2">
          <input value={newGroup} onChange={(e) => setNewGroup(e.target.value)} data-testid="admin-curr-new-group-name"
                 placeholder="Grup adı (ör. 1. Temel Matematik Konuları)"
                 className="flex-1 px-3 py-2 rounded-md bg-[#0A0A0A] border border-[#262626]"/>
          <button onClick={addGroup} data-testid="admin-curr-add-group"
                  className="px-4 py-2 rounded-md bg-[#EA580C] hover:bg-[#F97316] inline-flex items-center gap-2">
            <Plus size={16}/> Ekle
          </button>
        </div>
        <p className="text-xs text-slate-500 mt-2">
          Ana konuları grup başlığına sürükleyip bırakarak atayabilirsin. Aynı grup içinde sıra için ana konuyu başka bir ana konunun üstüne bırak.
        </p>
      </section>

      <div className="space-y-3">
        {tree.groups.map((g, gi) => {
          const isOpen = open[g.group_id] !== false;
          return (
            <div key={g.group_id} data-testid={`admin-curr-group-${g.group_id}`}
                 className="rounded-lg border border-[#262626] bg-[#0F0F0F]"
                 onDragOver={onDragOver} onDrop={dropOnGroup(g)}>
              <div className="flex items-center gap-2 p-3 border-b border-[#262626] bg-[#171717]"
                   draggable onDragStart={onDragStart("group", g.group_id)}>
                <GripVertical size={14} className="text-slate-500 cursor-grab"/>
                <button onClick={() => setOpen((m) => ({ ...m, [g.group_id]: !isOpen }))}
                        className="p-1" data-testid={`admin-curr-group-toggle-${g.group_id}`}>
                  {isOpen ? <ChevronDown size={16}/> : <ChevronRight size={16}/>}
                </button>
                <div className="flex-1 min-w-0">
                  <div className="text-xs text-slate-500 font-mono">Grup #{gi + 1} · {g.exam_level}</div>
                  <div className="font-medium truncate">{g.name}</div>
                </div>
                <button onClick={() => renameGroup(g)} data-testid={`admin-curr-group-rename-${g.group_id}`}
                        className="px-2 py-1 rounded border border-[#262626] text-xs">Yeniden Adlandır</button>
                <button onClick={() => deleteGroup(g)} data-testid={`admin-curr-group-delete-${g.group_id}`}
                        className="p-1.5 rounded border border-rose-500/40 text-rose-300 hover:bg-rose-500/10">
                  <Trash2 size={12}/>
                </button>
              </div>
              {isOpen && (
                <div className="p-3 space-y-2">
                  {(g.topics || []).length === 0 && (
                    <div className="text-xs text-slate-500 border border-dashed border-[#262626] rounded p-3 text-center">
                      Buraya ana konu sürükleyip bırak.
                    </div>
                  )}
                  {(g.topics || []).map((t) => (
                    <TopicRow key={t.topic_id} topic={t} siblings={g.topics}
                              onDragStart={onDragStart} onDragOver={onDragOver}
                              onDrop={dropOnTopic(t, g.topics)} onDropSub={dropOnSub}
                              onDetach={() => assignToGroup(t.topic_id, null)}/>
                  ))}
                </div>
              )}
            </div>
          );
        })}

        <div className="rounded-lg border border-dashed border-[#262626] bg-[#0A0A0A]"
             data-testid="admin-curr-ungrouped" onDragOver={onDragOver} onDrop={dropDetachToUngrouped}>
          <div className="p-3 border-b border-[#262626] text-sm text-slate-400">
            Gruba bağlı olmayan ana konular ({tree.ungrouped.length}) — buraya bırakırsan gruptan çıkar.
          </div>
          <div className="p-3 space-y-2">
            {tree.ungrouped.map((t) => (
              <TopicRow key={t.topic_id} topic={t} siblings={tree.ungrouped}
                        onDragStart={onDragStart} onDragOver={onDragOver}
                        onDrop={dropOnTopic(t, tree.ungrouped)} onDropSub={dropOnSub}
                        onDetach={null}/>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function TopicRow({ topic, siblings, onDragStart, onDragOver, onDrop, onDropSub, onDetach }) {
  const [open, setOpen] = useState(false);
  const subs = topic.subtopics || [];
  return (
    <div className="rounded-md border border-[#262626] bg-[#171717]"
         draggable onDragStart={onDragStart("topic", topic.topic_id)}
         onDragOver={onDragOver} onDrop={onDrop}
         data-testid={`admin-curr-topic-${topic.topic_id}`}>
      <div className="flex items-center gap-2 p-2">
        <GripVertical size={14} className="text-slate-500 cursor-grab"/>
        <button onClick={() => setOpen((v) => !v)} className="p-1">
          {open ? <ChevronDown size={14}/> : <ChevronRight size={14}/>}
        </button>
        <div className="flex-1 min-w-0">
          <div className="text-sm truncate">{topic.name}</div>
          <div className="text-xs text-slate-500 font-mono">{topic.slug} · {subs.length} alt</div>
        </div>
        {topic.is_active === false && <span className="text-xs px-2 py-0.5 rounded-full bg-slate-700">Pasif</span>}
        {onDetach && (
          <button onClick={onDetach} data-testid={`admin-curr-topic-detach-${topic.topic_id}`}
                  className="px-2 py-1 rounded border border-[#262626] text-xs">Gruptan Çıkar</button>
        )}
      </div>
      {open && subs.length > 0 && (
        <ul className="border-t border-[#262626] p-2 space-y-1">
          {subs.map((s) => (
            <li key={s.subtopic_id} data-testid={`admin-curr-subtopic-${s.subtopic_id}`}
                draggable onDragStart={onDragStart("sub", s.subtopic_id)}
                onDragOver={onDragOver} onDrop={onDropSub(s, subs)}
                className="flex items-center gap-2 px-2 py-1 rounded bg-[#0A0A0A] border border-[#262626]">
              <GripVertical size={12} className="text-slate-600 cursor-grab"/>
              <span className="text-sm flex-1 truncate">{s.name}</span>
              <span className="text-xs text-slate-500 font-mono">{s.slug}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
